import { createApi } from '@reduxjs/toolkit/query/react';
import { ProductionType } from '@types';
import { baseQueryWithReauth } from '../utils/prepHeaders';
import { ApiType } from '../utils/typeTransformer';
import { QualityControlApi } from './qualityControlApi';
import { ReleasesApi } from './releasesApi';

export type args = {
  getWorkOrderFrames: ApiType<ProductionType.WorkOrderFrames.GetFrames>['args'];
  postWorkOrder: ApiType<ProductionType.WorkOrders.Post>['args'];
  patchWorkOrderBulkAssign: ApiType<ProductionType.WorkOrders.BulkAssign>['args'];
  postBulkCompleteFrameStep: ApiType<ProductionType.WorkOrders.BulkComplete>['args'];
  getAllWorkOrders: ApiType<ProductionType.WorkOrders.Get>['args'];
  getFrame: ApiType<ProductionType.WorkOrderFrames.GetFrame>['args'];
  getFrameDetails: ApiType<ProductionType.WorkOrderFrames.GetFrameDetails>['args'];
  getFrameByWorkOrderStep: ApiType<ProductionType.WorkOrderFrames.GetFramesByStep>['args'];
  getFramesByRelease: ApiType<ProductionType.WorkOrderFrames.GetFramesByRelease>['args'];
  postCompleteFrameStep: ApiType<ProductionType.WorkOrderFrames.PostCompleteFrameStep>['args'];
  getWorkOrderStepChecklist: ApiType<ProductionType.WorkOrderChecklist.Get>['args'];
  putWorkOrderStepChecklist: ApiType<ProductionType.WorkOrderChecklist.Put>['args'];
  deleteWorkOrderStepChecklist: ApiType<ProductionType.WorkOrderChecklist.Delete>['args'];

  // Packing Slips
  postPackingSlips: ApiType<ProductionType.PackingSlip.PostPackingSlips>['args'];
  getPackingSlipsAll: ApiType<ProductionType.PackingSlip.GetPackingSlips>['args'];
  getPackingSlipEligibleFrames: ApiType<ProductionType.PackingSlip.GetPackingSlipEligibleFrames>['args'];
  postPackingSlipFrames: ApiType<ProductionType.PackingSlip.PostPackingSlipFrames>['args'];
  getPackingSlipFrames: ApiType<ProductionType.PackingSlip.GetPackingSlipFrames>['args'];
  deletePackingSlipFrames: ApiType<ProductionType.PackingSlip.DeletePackingSlipFrames>['args'];

  // Shipping Lists
  postShippingLists: ApiType<ProductionType.ShippingList.PostShippingLists>['args'];
  getShippingListsAll: ApiType<ProductionType.ShippingList.GetShippingLists>['args'];
  getShippingListPackingSlips: ApiType<ProductionType.ShippingList.GetShippingListPackingSlips>['args'];
  getShippingListEligibleFrames: ApiType<ProductionType.ShippingList.GetShippingListEligibleFrames>['args'];
  postShippingListFrames: ApiType<ProductionType.ShippingList.PostShippingListFrames>['args'];
  getShippingListFrames: ApiType<ProductionType.ShippingList.GetShippingListFrames>['args'];
  deleteShippingListFrames: ApiType<ProductionType.ShippingList.DeleteShippingListFrames>['args'];

  // Crates
  getCrates: ApiType<ProductionType.Crate.GetCrates>['args'];
  postCrate: ApiType<ProductionType.Crate.PostCrate>['args'];
  getCrateEligibleFrames: ApiType<ProductionType.Crate.GetCrateEligibleFrames>['args'];
  getCrate: ApiType<ProductionType.Crate.GetCrate>['args'];
  patchCrate: ApiType<ProductionType.Crate.PatchCrate>['args'];
  deleteCrate: ApiType<ProductionType.Crate.DeleteCrate>['args'];
  postCrateItems: ApiType<ProductionType.Crate.PostCrateItems>['args'];
  deleteCrateItems: ApiType<ProductionType.Crate.DeleteCrateItems>['args'];
  exportCrateSheet: ApiType<ProductionType.Crate.ExportCrateSheet>['args'];
  exportPackingList: ApiType<ProductionType.Crate.ExportPackingList>['args'];

  // Shipments
  getShipments: ApiType<ProductionType.Shipment.GetShipments>['args'];
  postShipment: ApiType<ProductionType.Shipment.PostShipment>['args'];
  getAvailableCrates: ApiType<ProductionType.Shipment.GetAvailableCrates>['args'];
  getShipment: ApiType<ProductionType.Shipment.GetShipment>['args'];
  patchShipment: ApiType<ProductionType.Shipment.PatchShipment>['args'];
  deleteShipment: ApiType<ProductionType.Shipment.DeleteShipment>['args'];
  postShipmentItems: ApiType<ProductionType.Shipment.PostShipmentItems>['args'];
  deleteShipmentItems: ApiType<ProductionType.Shipment.DeleteShipmentItems>['args'];
  exportShipmentSheet: ApiType<ProductionType.Shipment.ExportShipmentSheet>['args'];
};
export type responses = {
  getWorkOrderFrames: ApiType<ProductionType.WorkOrderFrames.GetFrames>['response'];
  postWorkOrder: ApiType<ProductionType.WorkOrders.Post>['response'];
  patchWorkOrderBulkAssign: ApiType<ProductionType.WorkOrders.BulkAssign>['response'];
  postBulkCompleteFrameStep: ApiType<ProductionType.WorkOrders.BulkComplete>['response'];
  getAllWorkOrders: ApiType<ProductionType.WorkOrders.Get>['response'];
  getFrame: ApiType<ProductionType.WorkOrderFrames.GetFrame>['response'];
  getFrameDetails: ApiType<ProductionType.WorkOrderFrames.GetFrameDetails>['response'];
  getFrameByWorkOrderStep: ApiType<ProductionType.WorkOrderFrames.GetFramesByStep>['response'];
  getFramesByRelease: ApiType<ProductionType.WorkOrderFrames.GetFramesByRelease>['response'];
  postCompleteFrameStep: ApiType<ProductionType.WorkOrderFrames.PostCompleteFrameStep>['response'];
  getWorkOrderStepChecklist: ApiType<ProductionType.WorkOrderChecklist.Get>['response'];
  putWorkOrderStepChecklist: ApiType<ProductionType.WorkOrderChecklist.Put>['response'];
  deleteWorkOrderStepChecklist: void;

  // Packing Slips
  postPackingSlips: ApiType<ProductionType.PackingSlip.PostPackingSlips>['response'];
  getPackingSlipsAll: ApiType<ProductionType.PackingSlip.GetPackingSlips>['response'];
  getPackingSlipEligibleFrames: ApiType<ProductionType.PackingSlip.GetPackingSlipEligibleFrames>['response'];
  postPackingSlipFrames: ApiType<ProductionType.PackingSlip.PostPackingSlipFrames>['response'];
  getPackingSlipFrames: ApiType<ProductionType.PackingSlip.GetPackingSlipFrames>['response'];
  deletePackingSlipFrames: void;

  // Shipping Lists
  postShippingLists: ApiType<ProductionType.ShippingList.PostShippingLists>['response'];
  getShippingListsAll: ApiType<ProductionType.ShippingList.GetShippingLists>['response'];
  getShippingListPackingSlips: ApiType<ProductionType.ShippingList.GetShippingListPackingSlips>['response'];
  getShippingListEligibleFrames: ApiType<ProductionType.ShippingList.GetShippingListEligibleFrames>['response'];
  postShippingListFrames: ApiType<ProductionType.ShippingList.PostShippingListFrames>['response'];
  getShippingListFrames: ApiType<ProductionType.ShippingList.GetShippingListFrames>['response'];
  deleteShippingListFrames: void;

  // Crates
  getCrates: ApiType<ProductionType.Crate.GetCrates>['response'];
  postCrate: ApiType<ProductionType.Crate.PostCrate>['response'];
  getCrateEligibleFrames: ApiType<ProductionType.Crate.GetCrateEligibleFrames>['response'];
  getCrate: ApiType<ProductionType.Crate.GetCrate>['response'];
  patchCrate: ApiType<ProductionType.Crate.PatchCrate>['response'];
  deleteCrate: void;
  postCrateItems: ApiType<ProductionType.Crate.PostCrateItems>['response'];
  deleteCrateItems: ApiType<ProductionType.Crate.DeleteCrateItems>['response'];
  exportCrateSheet: Blob;
  exportPackingList: Blob;

  // Shipments
  getShipments: ApiType<ProductionType.Shipment.GetShipments>['response'];
  postShipment: ApiType<ProductionType.Shipment.PostShipment>['response'];
  getAvailableCrates: ApiType<ProductionType.Shipment.GetAvailableCrates>['response'];
  getShipment: ApiType<ProductionType.Shipment.GetShipment>['response'];
  patchShipment: ApiType<ProductionType.Shipment.PatchShipment>['response'];
  deleteShipment: void;
  postShipmentItems: ApiType<ProductionType.Shipment.PostShipmentItems>['response'];
  deleteShipmentItems: ApiType<ProductionType.Shipment.DeleteShipmentItems>['response'];
  exportShipmentSheet: Blob;
};

const keepUnusedDataFor = 86400;

export const productionApi = createApi({
  reducerPath: 'productionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Production'],

  endpoints: (builder) => ({
    getWorkOrderFrames: builder.query<
      responses['getWorkOrderFrames'],
      args['getWorkOrderFrames']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/createWO/frames/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frames-${_arg.params.project_id}-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    postWorkOrder: builder.mutation<
      responses['postWorkOrder'],
      args['postWorkOrder']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/workorders/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
            ]
          : [],
    }),

    patchWorkOrderBulkAssign: builder.mutation<
      responses['patchWorkOrderBulkAssign'],
      args['patchWorkOrderBulkAssign']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/workorders/bulk-assign/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'work-orders' },
              {
                type: 'Production',
                id: `work-orders-${_arg.params.project_id}-${_arg.params.release_id}`,
              },
            ]
          : [],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            ReleasesApi.Api.util.invalidateTags([
              {
                type: 'Releases',
                id: `summary-views-release-${arg.params.release_id}`,
              },
            ]),
          );
        } catch {
          // handled by global RTK error middleware
        }
      },
    }),

    getAllWorkOrders: builder.query<
      responses['getAllWorkOrders'],
      args['getAllWorkOrders']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/workorders/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-orders-${_arg.params.project_id}-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    getFrame: builder.query<responses['getFrame'], args['getFrame']>({
      query: ({ params }) => ({
        url: `/production/frames/${params.id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frame-${_arg.params.id}`,
              },
              {
                type: 'Production',
                id: 'frame-drawing-consumers',
              },
            ]
          : [],
    }),

    getFrameDetails: builder.query<
      responses['getFrameDetails'],
      args['getFrameDetails']
    >({
      query: ({ params }) => ({
        url: `/production/frames/${params.frame_id}/detail/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frame-details-${_arg.params.frame_id}`,
              },
              {
                type: 'Production',
                id: 'frame-drawing-consumers',
              },
            ]
          : [],
    }),

    getFramesByWorkOrderStep: builder.query<
      responses['getFrameByWorkOrderStep'],
      args['getFrameByWorkOrderStep']
    >({
      query: ({ params, query }) => ({
        url: `/production/releases/${params.release_id}/frames/step/${params.step}/`,
        method: 'GET',
        params: query,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frames-by-step-${_arg.params.release_id}-${_arg.params.step}`,
              },
            ]
          : [],
    }),

    getFramesByRelease: builder.query<
      responses['getFramesByRelease'],
      args['getFramesByRelease']
    >({
      query: ({ params }) => ({
        url: `/production/releases/${params.release_id}/frames/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frames-by-release-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    postCompleteFrameStep: builder.mutation<
      responses['postCompleteFrameStep'],
      args['postCompleteFrameStep']
    >({
      query: ({ body, params }) => ({
        url: `/production/workorders/${params.frame_id}/complete/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-frame-${_arg.params.frame_id}`,
              },
              {
                type: 'Production',
                id: 'shipping-lists',
              },
            ]
          : [],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            QualityControlApi.Api.util.invalidateTags([
              { type: 'QualityControl', id: 'events-all' },
            ]),
          );
          dispatch(ReleasesApi.Api.util.invalidateTags([{ type: 'Releases' }]));
        } catch {
          // handled by global RTK error middleware
        }
      },
    }),

    postBulkCompleteFrameStep: builder.mutation<
      responses['postBulkCompleteFrameStep'],
      args['postBulkCompleteFrameStep']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/workorders/bulk-complete/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          (response.status === 200 || response.status === 207) &&
          !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Production', id: 'work-orders' },
        {
          type: 'Production',
          id: `work-orders-${_arg.params.project_id}-${_arg.params.release_id}`,
        },
        {
          type: 'Production',
          id: `work-order-frames-by-step-${_arg.params.release_id}-${_arg.body.step}`,
        },
        { type: 'Production', id: 'shipping-lists' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            ReleasesApi.Api.util.invalidateTags([
              {
                type: 'Releases',
                id: `summary-views-release-${arg.params.release_id}`,
              },
            ]),
          );
          dispatch(
            QualityControlApi.Api.util.invalidateTags([
              { type: 'QualityControl', id: 'events-all' },
            ]),
          );
        } catch {
          // handled by global RTK error middleware
        }
      },
    }),

    getWorkOrderStepChecklist: builder.query<
      responses['getWorkOrderStepChecklist'],
      args['getWorkOrderStepChecklist']
    >({
      query: ({ params }) => ({
        url: `/production/checklist/${params.step}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          (response.status === 200 && !result.isError) ||
          response.status === 404,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: `work-order-step-checklist-${_arg.params.step}`,
              },
            ]
          : [],
    }),

    putWorkOrderStepChecklist: builder.mutation<
      responses['putWorkOrderStepChecklist'],
      args['putWorkOrderStepChecklist']
    >({
      query: ({ body, params }) => ({
        url: `/production/checklist/${params.step}/`,
        method: 'PUT',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: `work-order-step-checklist-${_arg.params.step}`,
              },
            ]
          : [],
    }),

    deleteWorkOrderStepChecklist: builder.mutation<
      responses['deleteWorkOrderStepChecklist'],
      args['deleteWorkOrderStepChecklist']
    >({
      query: ({ params }) => ({
        url: `/production/checklist/${params.step}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'Production',
          id: `work-order-step-checklist-${_arg.params.step}`,
        },
      ],
    }),

    // Packing Slips
    postPackingSlips: builder.mutation<
      responses['postPackingSlips'],
      args['postPackingSlips']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/packing-slips/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'packing-slips',
              },
            ]
          : [],
    }),

    getPackingSlipsAll: builder.query<
      responses['getPackingSlipsAll'],
      args['getPackingSlipsAll']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/packing-slips/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'packing-slips',
              },
              {
                type: 'Production',
                id: `packing-slip-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    getPackingSlipEligibleFrames: builder.query<
      responses['getPackingSlipEligibleFrames'],
      args['getPackingSlipEligibleFrames']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/packing-slips/frames/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'work-orders',
              },
              {
                type: 'Production',
                id: 'packing-slips',
              },
              {
                type: 'Production',
                id: `packing-slip-eligible-frames-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    postPackingSlipFrames: builder.mutation<
      responses['postPackingSlipFrames'],
      args['postPackingSlipFrames']
    >({
      query: ({ body, params }) => ({
        url: `/production/packing-slips/${params.packing_slip_id}/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'packing-slips',
              },
            ]
          : [],
    }),

    getPackingSlipFrames: builder.query<
      responses['getPackingSlipFrames'],
      args['getPackingSlipFrames']
    >({
      query: ({ params }) => ({
        url: `/production/packing-slips/${params.packing_slip_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'packing-slips',
              },
              {
                type: 'Production',
                id: `packing-slip-frame-${_arg.params.packing_slip_id}`,
              },
            ]
          : [],
    }),

    deletePackingSlipFrames: builder.mutation<
      responses['deletePackingSlipFrames'],
      args['deletePackingSlipFrames']
    >({
      query: ({ params, body }) => ({
        url: `/production/packing-slips/${params.packing_slip_id}/items/bulk-delete/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'Production',
          id: 'packing-slips',
        },
        {
          type: 'Production',
          id: 'work-orders',
        },
      ],
    }),

    // Shipping lists
    postShippingLists: builder.mutation<
      responses['postShippingLists'],
      args['postShippingLists']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/shipping-lists/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'Production',
          id: 'shipping-lists',
        },
      ],
    }),

    getShippingListsAll: builder.query<
      responses['getShippingListsAll'],
      args['getShippingListsAll']
    >({
      query: ({ params }) => ({
        url: `/production/projects/${params.project_id}/releases/${params.release_id}/shipping-lists/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'shipping-lists',
              },
              {
                type: 'Production',
                id: `shipping-lists-${_arg.params.project_id}-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    getShippingListPackingSlips: builder.query<
      responses['getShippingListPackingSlips'],
      args['getShippingListPackingSlips']
    >({
      query: ({ params }) => ({
        url: `/production/releases/${params.release_id}/packing-slips`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'shipping-lists',
              },
              {
                type: 'Production',
                id: `shipping-list-packing-slips-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    getShippingListEligibleFrames: builder.query<
      responses['getShippingListEligibleFrames'],
      args['getShippingListEligibleFrames']
    >({
      query: ({ params }) => ({
        url: `/production/releases/${params.release_id}/packing-slips/${params.packing_slip_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'shipping-lists',
              },
              {
                type: 'Production',
                id: `shipping-list-eligible-frames-${_arg.params.release_id}`,
              },
            ]
          : [],
    }),

    postShippingListFrames: builder.mutation<
      responses['postShippingListFrames'],
      args['postShippingListFrames']
    >({
      query: ({ body, params }) => ({
        url: `/production/shipping-lists/${params.shipping_list_id}/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'Production',
          id: 'shipping-lists',
        },
      ],
    }),

    getShippingListFrames: builder.query<
      responses['getShippingListFrames'],
      args['getShippingListFrames']
    >({
      query: ({ params }) => ({
        url: `/production/shipping-lists/${params.shipping_list_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              {
                type: 'Production',
                id: 'shipping-lists',
              },
              {
                type: 'Production',
                id: `shipping-list-frames-${_arg.params.shipping_list_id}`,
              },
            ]
          : [],
    }),

    deleteShippingListFrames: builder.mutation<
      responses['deleteShippingListFrames'],
      args['deleteShippingListFrames']
    >({
      query: ({ params, body }) => ({
        url: `/production/shipping-lists/${params.shipping_list_id}/items/bulk-delete/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        {
          type: 'Production',
          id: 'shipping-lists',
        },
        {
          type: 'Production',
          id: 'packing-slips',
        },
      ],
    }),

    // -------------------------------------------------------------------
    //     CRATES (primary packing layer)
    // -------------------------------------------------------------------
    getCrates: builder.query<responses['getCrates'], args['getCrates']>({
      query: ({ params, query }) => {
        const search = new URLSearchParams();
        if (query?.release) search.set('release', String(query.release));
        if (query?.status) search.set('status', query.status);
        const qs = search.toString();
        return {
          url: `/production/projects/${params.project_id}/crates/${qs ? `?${qs}` : ''}`,
          method: 'GET',
          validateStatus: (response, result) =>
            response.status === 200 && !result.isError,
        };
      },
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'crates' },
              {
                type: 'Production',
                id: `crates-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    postCrate: builder.mutation<responses['postCrate'], args['postCrate']>({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/crates/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result) =>
        _result ? [{ type: 'Production', id: 'crates' }] : [],
    }),

    getCrateEligibleFrames: builder.query<
      responses['getCrateEligibleFrames'],
      args['getCrateEligibleFrames']
    >({
      query: ({ params, query }) => {
        const search = new URLSearchParams();
        if (query?.release) search.set('release', String(query.release));
        const qs = search.toString();
        return {
          url: `/production/projects/${params.project_id}/crates/eligible-frames/${qs ? `?${qs}` : ''}`,
          method: 'GET',
          validateStatus: (response, result) =>
            response.status === 200 && !result.isError,
        };
      },
      keepUnusedDataFor,
      providesTags: (_result) =>
        _result
          ? [
              { type: 'Production', id: 'work-orders' },
              { type: 'Production', id: 'crates' },
              { type: 'Production', id: 'crate-eligible-frames' },
            ]
          : [],
    }),

    getCrate: builder.query<responses['getCrate'], args['getCrate']>({
      query: ({ params }) => ({
        url: `/production/crates/${params.crate_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'crates' },
              { type: 'Production', id: `crate-${_arg.params.crate_id}` },
            ]
          : [],
    }),

    patchCrate: builder.mutation<responses['patchCrate'], args['patchCrate']>({
      query: ({ body, params }) => ({
        url: `/production/crates/${params.crate_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'crates' },
              { type: 'Production', id: 'shipments' },
              { type: 'Production', id: `crate-${_arg.params.crate_id}` },
            ]
          : [],
    }),

    deleteCrate: builder.mutation<
      responses['deleteCrate'],
      args['deleteCrate']
    >({
      query: ({ params }) => ({
        url: `/production/crates/${params.crate_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: () => [
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    postCrateItems: builder.mutation<
      responses['postCrateItems'],
      args['postCrateItems']
    >({
      query: ({ body, params }) => ({
        url: `/production/crates/${params.crate_id}/items/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'shipments' },
        { type: 'Production', id: `crate-${_arg.params.crate_id}` },
        { type: 'Production', id: 'crate-eligible-frames' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    deleteCrateItems: builder.mutation<
      responses['deleteCrateItems'],
      args['deleteCrateItems']
    >({
      query: ({ body, params }) => ({
        url: `/production/crates/${params.crate_id}/items/bulk-delete/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'shipments' },
        { type: 'Production', id: `crate-${_arg.params.crate_id}` },
        { type: 'Production', id: 'crate-eligible-frames' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    exportCrateSheet: builder.mutation<
      responses['exportCrateSheet'],
      args['exportCrateSheet']
    >({
      query: ({ params }) => ({
        url: `/production/crates/${params.crate_id}/export/`,
        method: 'POST',
        responseHandler: async (response) => await response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    exportPackingList: builder.mutation<
      responses['exportPackingList'],
      args['exportPackingList']
    >({
      query: ({ params, query }) => {
        const search = new URLSearchParams();
        if (query?.release) search.set('release', String(query.release));
        const qs = search.toString();
        return {
          url: `/production/projects/${params.project_id}/crates/packing-list/export/${qs ? `?${qs}` : ''}`,
          method: 'GET',
          responseHandler: async (response) => await response.blob(),
          validateStatus: (response) => response.status === 200,
        };
      },
    }),

    // -------------------------------------------------------------------
    //     SHIPMENTS (primary shipping layer)
    // -------------------------------------------------------------------
    getShipments: builder.query<
      responses['getShipments'],
      args['getShipments']
    >({
      query: ({ params, query }) => {
        const search = new URLSearchParams();
        if (query?.status) search.set('status', query.status);
        const qs = search.toString();
        return {
          url: `/production/projects/${params.project_id}/shipments/${qs ? `?${qs}` : ''}`,
          method: 'GET',
          validateStatus: (response, result) =>
            response.status === 200 && !result.isError,
        };
      },
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'shipments' },
              {
                type: 'Production',
                id: `shipments-${_arg.params.project_id}`,
              },
            ]
          : [],
    }),

    postShipment: builder.mutation<
      responses['postShipment'],
      args['postShipment']
    >({
      query: ({ body, params }) => ({
        url: `/production/projects/${params.project_id}/shipments/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: (_result) =>
        _result ? [{ type: 'Production', id: 'shipments' }] : [],
    }),

    getAvailableCrates: builder.query<
      responses['getAvailableCrates'],
      args['getAvailableCrates']
    >({
      query: ({ params, query }) => {
        const search = new URLSearchParams();
        if (query?.release) search.set('release', String(query.release));
        const qs = search.toString();
        return {
          url: `/production/projects/${params.project_id}/shipments/available-crates/${qs ? `?${qs}` : ''}`,
          method: 'GET',
          validateStatus: (response, result) =>
            response.status === 200 && !result.isError,
        };
      },
      keepUnusedDataFor,
      providesTags: (_result) =>
        _result
          ? [
              { type: 'Production', id: 'crates' },
              { type: 'Production', id: 'shipments' },
              { type: 'Production', id: 'available-crates' },
            ]
          : [],
    }),

    getShipment: builder.query<responses['getShipment'], args['getShipment']>({
      query: ({ params }) => ({
        url: `/production/shipments/${params.shipment_id}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'shipments' },
              {
                type: 'Production',
                id: `shipment-${_arg.params.shipment_id}`,
              },
            ]
          : [],
    }),

    patchShipment: builder.mutation<
      responses['patchShipment'],
      args['patchShipment']
    >({
      query: ({ body, params }) => ({
        url: `/production/shipments/${params.shipment_id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _arg) =>
        _result
          ? [
              { type: 'Production', id: 'shipments' },
              {
                type: 'Production',
                id: `shipment-${_arg.params.shipment_id}`,
              },
            ]
          : [],
    }),

    deleteShipment: builder.mutation<
      responses['deleteShipment'],
      args['deleteShipment']
    >({
      query: ({ params }) => ({
        url: `/production/shipments/${params.shipment_id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: () => [
        { type: 'Production', id: 'shipments' },
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    postShipmentItems: builder.mutation<
      responses['postShipmentItems'],
      args['postShipmentItems']
    >({
      query: ({ body, params }) => ({
        url: `/production/shipments/${params.shipment_id}/items/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Production', id: 'shipments' },
        { type: 'Production', id: `shipment-${_arg.params.shipment_id}` },
        { type: 'Production', id: 'available-crates' },
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    deleteShipmentItems: builder.mutation<
      responses['deleteShipmentItems'],
      args['deleteShipmentItems']
    >({
      query: ({ body, params }) => ({
        url: `/production/shipments/${params.shipment_id}/items/bulk-delete/`,
        method: 'POST',
        body,
        validateStatus: (response) =>
          response.status === 200 || response.status === 207,
      }),
      invalidatesTags: (_result, _error, _arg) => [
        { type: 'Production', id: 'shipments' },
        { type: 'Production', id: `shipment-${_arg.params.shipment_id}` },
        { type: 'Production', id: 'available-crates' },
        { type: 'Production', id: 'crates' },
        { type: 'Production', id: 'work-orders' },
      ],
    }),

    exportShipmentSheet: builder.mutation<
      responses['exportShipmentSheet'],
      args['exportShipmentSheet']
    >({
      query: ({ params }) => ({
        url: `/production/shipments/${params.shipment_id}/export/`,
        method: 'POST',
        responseHandler: async (response) => await response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),
  }),
});

export const {
  useGetWorkOrderFramesQuery,
  usePostWorkOrderMutation,
  usePatchWorkOrderBulkAssignMutation,
  usePostBulkCompleteFrameStepMutation,
  useGetAllWorkOrdersQuery,
  useGetFrameQuery,
  useGetFrameDetailsQuery,
  useGetFramesByWorkOrderStepQuery,
  useGetFramesByReleaseQuery,
  usePostCompleteFrameStepMutation,
  useGetWorkOrderStepChecklistQuery,
  usePutWorkOrderStepChecklistMutation,
  useDeleteWorkOrderStepChecklistMutation,

  // Packing Slips
  usePostPackingSlipsMutation,
  useGetPackingSlipsAllQuery,
  useGetPackingSlipEligibleFramesQuery,
  usePostPackingSlipFramesMutation,
  useGetPackingSlipFramesQuery,
  useDeletePackingSlipFramesMutation,

  // Shipping Lists
  usePostShippingListsMutation,
  useGetShippingListsAllQuery,
  useGetShippingListPackingSlipsQuery,
  useGetShippingListEligibleFramesQuery,
  usePostShippingListFramesMutation,
  useGetShippingListFramesQuery,
  useDeleteShippingListFramesMutation,

  // Crates
  useGetCratesQuery,
  usePostCrateMutation,
  useGetCrateEligibleFramesQuery,
  useGetCrateQuery,
  usePatchCrateMutation,
  useDeleteCrateMutation,
  usePostCrateItemsMutation,
  useDeleteCrateItemsMutation,
  useExportCrateSheetMutation,
  useExportPackingListMutation,

  // Shipments
  useGetShipmentsQuery,
  usePostShipmentMutation,
  useGetAvailableCratesQuery,
  useGetShipmentQuery,
  usePatchShipmentMutation,
  useDeleteShipmentMutation,
  usePostShipmentItemsMutation,
  useDeleteShipmentItemsMutation,
  useExportShipmentSheetMutation,
} = productionApi;

export namespace ProductionApi {
  export const Api = productionApi;
  export type Args = args;
  export type Responses = responses;
}
